import { CommonModule } from '@angular/common';
import { Component, inject, signal, type WritableSignal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { LottieComponent, type AnimationOptions } from 'ngx-lottie';
import { Navbar } from '../../../../shared/components/navbar/navbar';
import { Card } from '../../../../shared/components/card/card/card';
import { CardHeader } from '../../../../shared/components/card/card-header/card-header';
import { ReportsService } from '../../../../services/reports.service';
import type { LastDeposit } from '../../../../interfaces/reports';
import type { MenuItem } from 'primeng/api';
import { PaginatorModule, type PaginatorState } from 'primeng/paginator';
import { InputNumberModule } from 'primeng/inputnumber';
import { TagModule } from 'primeng/tag';

@Component({
  selector: 'app-deposits',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    DatePickerModule,
    FloatLabelModule,
    InputTextModule,
    ProgressSpinnerModule,
    TooltipModule,
    MenuModule,
    LottieComponent,
    Navbar,
    Card,
    CardHeader,
    PaginatorModule,
    InputNumberModule,
    TagModule,
  ],
  templateUrl: './deposits.html',
  styleUrl: './deposits.css',
})
export class Deposits {
  public emptyLottieOptions: AnimationOptions = { path: 'assets/lottie/empty.json', loop: true, autoplay: true };

  private readonly _reportsService = inject(ReportsService);

  public loading: WritableSignal<boolean> = signal(false);
  public firstRequest: WritableSignal<boolean> = signal(true);
  public data: WritableSignal<LastDeposit[]> = signal([]);
  public filteredData: WritableSignal<LastDeposit[]> = signal([]);
  public paginatedData: WritableSignal<LastDeposit[]> = signal([]);
  public searchTerm: WritableSignal<string> = signal('');

  public totalDeposits: WritableSignal<number> = signal(0);
  public totalValue: WritableSignal<number> = signal(0);
  public avgValue: WritableSignal<number> = signal(0);

  public page = 0;
  public rows = 10;
  public totalRecords = 0;

  public filterForm = new FormGroup({
    initial_date: new FormControl<Date | null>(null),
  });

  public exportMenuItems: MenuItem[] = [
    {
      label: 'Exportar CSV',
      icon: 'fa-solid fa-file-csv',
      command: () => this.exportCsv(),
    },
  ];

  ngOnInit(): void {
    const today = new Date();
    this.filterForm.patchValue({ initial_date: today });
    this.getData();
  }

  public getData(): void {
    const { initial_date } = this.filterForm.value;
    if (!initial_date) return;

    const filters: any = {
      initial_date: this._formatDate(initial_date),
      format: 'json',
    };

    this.loading.set(true);
    this.firstRequest.set(false);

    this._reportsService.lastDeposits(filters)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.data.set(response.data);
            this._calculateMetrics(response.data);
            this._applyFilter();
          }
        },
      });
  }

  public onSearch(term: string): void {
    this.searchTerm.set(term);
    this.page = 0;
    this._applyFilter();
  }

  public onPageChange(event: PaginatorState): void {
    this.page = event.page || 0;
    this.rows = event.rows || 10;
    this._paginateData();
  }

  public exportCsv(): void {
    const items = this.filteredData();
    if (!items.length) return;

    const headers = ['ID Jogador', 'CPF', 'Nome', 'Email', 'Data Último Depósito', 'Valor Último Depósito'];
    const csvRows = [
      headers.join(';'),
      ...items.map(item =>
        [
          item.id_jogador,
          this._formatCpf(item.cpf),
          item.nome,
          item.email,
          item.data_ult_deposito,
          item.valor_ultimo_deposito.toFixed(2).replace('.', ','),
        ].join(';')
      ),
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-depositos-${this._formatDate(new Date())}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  public formatCpf(cpf: string): string {
    return this._formatCpf(cpf);
  }

  private _formatCpf(cpf: string): string {
    if (!cpf || cpf.length !== 11) return cpf;
    return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
  }

  private _formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private _calculateMetrics(data: LastDeposit[]): void {
    const total = data.length;
    const sum = data.reduce((acc, item) => acc + item.valor_ultimo_deposito, 0);
    this.totalDeposits.set(total);
    this.totalValue.set(sum);
    this.avgValue.set(total > 0 ? sum / total : 0);
  }

  private _applyFilter(): void {
    const term = this.searchTerm().toLowerCase().trim();
    let filtered = this.data();

    if (term) {
      filtered = filtered.filter(item =>
        item.nome.toLowerCase().includes(term) ||
        item.cpf.includes(term) ||
        item.email.toLowerCase().includes(term) ||
        item.id_jogador.toString().includes(term)
      );
    }

    this.filteredData.set(filtered);
    this.totalRecords = filtered.length;
    this._calculateMetrics(filtered);
    this._paginateData();
  }

  private _paginateData(): void {
    const start = this.page * this.rows;
    const end = start + this.rows;
    this.paginatedData.set(this.filteredData().slice(start, end));
  }
}
